import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { services, ServiceType, getCityFromSlug } from './routes/seoRoutes';
import { Home } from './components/Home';
import { TestPage } from './components/TestPage';
import { Toaster } from 'react-hot-toast';
import { AppProvider } from './context/AppContext';

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Helper function to determine service and city from pathname
const getServiceAndCityFromPath = (pathname: string): { service?: ServiceType; city?: string } => {
  // Sort services by path length (longest first) to avoid partial matches
  const sortedServices = Object.entries(services).sort(([,a], [,b]) => b.path.length - a.path.length);
  
  for (const [serviceKey, serviceConfig] of sortedServices) {
    const pattern = `/${serviceConfig.path}-`;
    if (pathname.startsWith(pattern)) {
      const citySlug = pathname.replace(pattern, '');
      const city = getCityFromSlug(citySlug);
      return {
        service: serviceKey as ServiceType,
        city: city || undefined
      };
    }
  }
  
  return {};
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const { service, city } = getServiceAndCityFromPath(location.pathname);

  return (
    <AppProvider service={service} city={city}>
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow">
          <Routes>
            {/* Homepage */}
            <Route path="/" element={<Home />} />
            
            {/* Test page */}
            <Route path="/test-:city" element={<TestPage />} />
            
            {/* Service pages - all handled by Home component */}
            {/* Sort by path length (longest first) to avoid partial matches */}
            {Object.values(services)
              .sort((a, b) => b.path.length - a.path.length)
              .map((serviceConfig) => (
              <Route
                key={serviceConfig.path}
                path={`/${serviceConfig.path}-:city`}
                element={<Home />}
              />
            ))}
            
            {/* Fallback to home */}
            <Route path="*" element={<Home />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </AppProvider>
  );
};

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <Router>
        <Toaster />
        <ScrollToTop />
        <AppContent />
      </Router>
    </HelmetProvider>
  );
};

export default App;